/**
 * Incremental MD5 (RFC 1321) for upload checksums — videoBegin wants the hex md5 of the file and WebCrypto has no MD5.
 * Transfer checksum only, never used for security. Pure; feed chunks with update(), then hex().
 */
const S = [7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
  4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21];
const K = Array.from({ length: 64 }, (_, i) => Math.floor(Math.abs(Math.sin(i + 1)) * 2 ** 32) >>> 0);

export class Md5 {
  private h = [0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476];
  private buf = new Uint8Array(64);
  private bufLen = 0;
  private length = 0;
  private readonly w = new Uint32Array(16);

  update(data: Uint8Array): this {
    let i = 0;
    this.length += data.length;
    if (this.bufLen) {
      const take = Math.min(64 - this.bufLen, data.length);
      this.buf.set(data.subarray(0, take), this.bufLen);
      this.bufLen += take;
      i = take;
      if (this.bufLen < 64) return this;
      this.block(this.buf, 0);
      this.bufLen = 0;
    }
    for (; i + 64 <= data.length; i += 64) this.block(data, i);
    if (i < data.length) {
      this.buf.set(data.subarray(i), 0);
      this.bufLen = data.length - i;
    }
    return this;
  }

  hex(): string {
    const bits = this.length * 8;
    const pad = new Uint8Array((this.bufLen < 56 ? 56 : 120) - this.bufLen + 8);
    pad[0] = 0x80;
    const lo = bits >>> 0;
    const hi = Math.floor(bits / 2 ** 32) >>> 0;
    for (let j = 0; j < 4; j++) {
      pad[pad.length - 8 + j] = (lo >>> (8 * j)) & 0xff;
      pad[pad.length - 4 + j] = (hi >>> (8 * j)) & 0xff;
    }
    const length = this.length;
    this.update(pad);
    this.length = length;
    return this.h.map((v) => [0, 8, 16, 24].map((s) => ((v >>> s) & 0xff).toString(16).padStart(2, "0")).join("")).join("");
  }

  private block(d: Uint8Array, at: number) {
    const w = this.w;
    for (let j = 0; j < 16; j++) w[j] = d[at + j * 4] | (d[at + j * 4 + 1] << 8) | (d[at + j * 4 + 2] << 16) | (d[at + j * 4 + 3] << 24);
    let [a, b, c, e] = this.h;
    for (let j = 0; j < 64; j++) {
      let f: number;
      let g: number;
      if (j < 16) {
        f = (b & c) | (~b & e);
        g = j;
      } else if (j < 32) {
        f = (e & b) | (~e & c);
        g = (5 * j + 1) % 16;
      } else if (j < 48) {
        f = b ^ c ^ e;
        g = (3 * j + 5) % 16;
      } else {
        f = c ^ (b | ~e);
        g = (7 * j) % 16;
      }
      const t = e;
      e = c;
      c = b;
      const x = (a + f + K[j] + w[g]) >>> 0;
      b = (b + ((x << S[j]) | (x >>> (32 - S[j])))) >>> 0;
      a = t;
    }
    this.h = [(this.h[0] + a) >>> 0, (this.h[1] + b) >>> 0, (this.h[2] + c) >>> 0, (this.h[3] + e) >>> 0];
  }
}

export const md5Hex = (data: Uint8Array) => new Md5().update(data).hex();
